/* eslint-disable import/no-cycle */
import React from "react";
import PropTypes from "prop-types";
import { Card } from "../Card";
import { CardBuilder, LogoPositions } from "../CardComponents/CardBuilder";
import { HeadingAsElement, HeadingSize } from "../../Heading";
import { CardAsType, CardShape } from "../CardComponents/CardType/CardType";
import * as styles from "./Primary.module.scss";

const PrimaryTheme = {
	coral: "coral",
	sea: "sea",
	royal: "royal",
	pink: "pink",
};

const PrimaryCard = ({
	className,
	clickable,
	type,
	shape,
	theme,
	image,
	images,
	logo,
	logoPosition,
	heading,
	headingAs,
	headingSize,
	content,
	subheader,
	link,
	button,
	hideLink,
	renderImage,
	renderLink,
}) => (
	<CardBuilder
		className={className}
		clickable={clickable}
		type={type}
		shape={shape}
		theme={theme}
		images={[image, ...images]}
		logo={logo}
		hideLink={hideLink}
		link={link}
		logoPosition={logoPosition}
		renderLink={renderLink}
		renderImage={renderImage}>
		<div className={styles.primary}>
			<Card.Header size={headingSize} as={headingAs} className={styles.primaryHeading}>
				{heading}
			</Card.Header>
			<div className={styles.primaryBody}>
				<div>
					{subheader && (
						<Card.Subheader className={styles.primarySubheading}>{subheader}</Card.Subheader>
					)}
					{content && (
						<Card.Content
							className={styles.primaryContent}
							dangerouslySetInnerHTML={{ __html: content }}
						/>
					)}
				</div>
				{(button?.to || link?.to) && (
					<Card.Button
						to={button?.to}
						target={button?.target}
						label={button?.text}
						size={button?.size || "sm"}
						theme={theme === "wedding" ? "wedding" : "primary"}
						className={styles.primaryButton}
						linkComponentProps={{ renderLink }}>
						{button?.text}
					</Card.Button>
				)}
			</div>
		</div>
	</CardBuilder>
);

export default PrimaryCard;
export { PrimaryCard, PrimaryTheme };

PrimaryCard.propTypes = {
	className: PropTypes.string,
	heading: PropTypes.string.isRequired,
	headingAs: PropTypes.oneOf(Object.keys(HeadingAsElement)),
	headingSize: PropTypes.oneOf(Object.keys(HeadingSize)),
	image: PropTypes.shape({
		source: PropTypes.shape({
			src: PropTypes.string,
			srcSet: PropTypes.string,
			sizes: PropTypes.string,
		}),
		alt: PropTypes.string,
		src: PropTypes.string,
	}),
	images: PropTypes.arrayOf(
		PropTypes.shape({
			fluid: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
			fixed: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
			source: PropTypes.shape({
				src: PropTypes.string,
				srcSet: PropTypes.string,
				sizes: PropTypes.string,
			}),
			alt: PropTypes.string,
			src: PropTypes.string,
		})
	),
	logo: PropTypes.shape({
		alt: PropTypes.string,
		src: PropTypes.string,
	}),
	logoPosition: PropTypes.oneOf(Object.keys(LogoPositions)),
	renderImage: PropTypes.func,
	renderLink: PropTypes.func,
	button: PropTypes.shape({
		icon: PropTypes.string,
		iconPlacement: PropTypes.oneOf(["right", "left"]),
		size: PropTypes.oneOf(["md", "sm"]),
		onClick: PropTypes.func,
		text: PropTypes.string,
		to: PropTypes.string,
		target: PropTypes.string,
	}),
	link: PropTypes.shape({
		text: PropTypes.string,
		to: PropTypes.string,
		target: PropTypes.string,
	}),
	content: PropTypes.string,
	subheader: PropTypes.string,
	hideLink: PropTypes.bool,
	/**
	 * CardBuilder props
	 */
	theme: PropTypes.oneOf(Object.keys(PrimaryTheme)),
	clickable: PropTypes.bool,
	type: PropTypes.oneOf(Object.keys(CardAsType)),
	shape: PropTypes.oneOf(Object.keys(CardShape)),
};

PrimaryCard.defaultProps = {
	className: undefined,
	headingAs: HeadingAsElement.h3,
	headingSize: HeadingSize.h2,
	subheader: undefined,
	logo: null,
	logoPosition: "bottomLeft",
	renderImage: undefined,
	image: null,
	images: [],
	renderLink: undefined,
	button: null,
	link: undefined,
	hideLink: false,
	content: "",
	clickable: true,
	type: "shadow",
	shape: "none",
	theme: "none",
};
